import React, { useState, useMemo } from 'react';
import { Project, Task } from '../types/Project';

import { EVMChart } from './EVMChart';
import { 
  calculateProjectEVM, 
  getMetricInterpretation, 
  formatCurrency,
  formatCurrencyRounded
} from '../utils/evmCalculations';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface EVMDashboardProps {
  projects: Project[];
  tasks: Task[];
  onBack: () => void;
}

export const EVMDashboard: React.FC<EVMDashboardProps> = ({
  projects,
  tasks,
  onBack
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  
  // 選択可能なプロジェクト（タスクがあるもののみ）
  const availableProjects = useMemo(() => {
    return projects.filter(project => 
      tasks.some(task => task.projectId === project.id)
    );
  }, [projects, tasks]);
  
  // 選択されたプロジェクトのEVMデータを計算
  const projectEVM = useMemo(() => {
    if (!selectedProjectId) return null;
    
    const project = projects.find(p => p.id === selectedProjectId);
    if (!project) return null;
    
    return calculateProjectEVM(project, tasks);
  }, [selectedProjectId, projects, tasks]);
  
  const interpretation = projectEVM ? getMetricInterpretation(projectEVM.metrics) : null;
  
  return (
    <div className="evm-dashboard">
      <div className="evm-header">
        <div className="evm-title-section">
          <div className="project-selector">
            <label htmlFor="project-select">分析対象プロジェクト:</label>
            <select
              id="project-select"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="project-select"
            >
              <option value="">プロジェクトを選択してください</option>
              {availableProjects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <h2>EVM分析</h2>
        </div>
      </div>
      
      {projectEVM ? (
        <div className="evm-content">
          {/* プロジェクト概要 */}
          <div className="evm-project-info">
            <h3>{projectEVM.projectName}</h3>
            <div className="project-dates">
              <span>期間: {format(projectEVM.startDate, 'yyyy/MM/dd', { locale: ja })} 〜 {format(projectEVM.endDate, 'yyyy/MM/dd', { locale: ja })}</span>
              <span>予算: {formatCurrency(projectEVM.totalBudget)}</span>
              <span>分析日: {format(projectEVM.currentDate, 'yyyy/MM/dd', { locale: ja })}</span>
            </div>
          </div>
          
          {/* 基本指標セクション */}
          <div className="evm-section">
            <h3>基本指標</h3>
            <div className="evm-metrics-grid">
              <div className="kpi-card pv">
                <h4>PV (計画価値)</h4>
                <div className="kpi-value">{formatCurrency(projectEVM.metrics.pv)}</div>
                <div className="kpi-description">予定された作業の価値</div>
              </div>
              
              <div className="kpi-card ev">
                <h4>EV (出来高価値)</h4>
                <div className="kpi-value">{formatCurrency(projectEVM.metrics.ev)}</div>
                <div className="kpi-description">完了した作業の価値</div>
              </div>
              
              <div className="kpi-card ac">
                <h4>AC (実績コスト)</h4>
                <div className="kpi-value">{formatCurrencyRounded(projectEVM.metrics.ac)}</div>
                <div className="kpi-description">実際に消費したコスト</div>
              </div>
            </div>
          </div>
          
          {/* 差異指標セクション */}
          <div className="evm-section">
            <h3>差異指標</h3>
            <div className="evm-metrics-grid">
              <div className="kpi-card cv">
                <h4>CV (コスト差異)</h4>
                <div className={`kpi-value ${projectEVM.metrics.cv >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrencyRounded(projectEVM.metrics.cv)}
                </div>
                <div className="kpi-description">{interpretation?.costStatus}</div>
              </div>
              
              <div className="kpi-card sv">
                <h4>SV (スケジュール差異)</h4>
                <div className={`kpi-value ${projectEVM.metrics.sv >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(projectEVM.metrics.sv)}
                </div>
                <div className="kpi-description">{interpretation?.scheduleStatus}</div>
              </div>
            </div>
          </div>
          
          {/* パフォーマンス指標セクション */}
          <div className="evm-section">
            <h3>パフォーマンス指標</h3>
            <div className="evm-metrics-grid">
              <div className="kpi-card cpi">
                <h4>CPI (コスト効率指数)</h4>
                <div className={`kpi-value ${projectEVM.metrics.cpi >= 1 ? 'positive' : 'negative'}`}>
                  {projectEVM.metrics.cpi.toFixed(2)}
                </div>
                <div className="kpi-description">{interpretation?.costEfficiency}</div>
              </div>
              
              <div className="kpi-card spi">
                <h4>SPI (スケジュール効率指数)</h4>
                <div className={`kpi-value ${projectEVM.metrics.spi >= 1 ? 'positive' : 'negative'}`}>
                  {projectEVM.metrics.spi.toFixed(2)}
                </div>
                <div className="kpi-description">{interpretation?.scheduleEfficiency}</div>
              </div>
              
              <div className="kpi-card progress">
                <h4>進捗率</h4>
                <div className="kpi-value">{projectEVM.metrics.progressPercentage.toFixed(1)}%</div>
                <div className="kpi-description">プロジェクト完了率</div>
              </div>
            </div>
          </div>
          
          {/* EVMトレンドチャートセクション */}
          <div className="evm-section">
            <h3>EVMトレンド分析</h3>
            <div className="evm-chart-container">
              <EVMChart data={projectEVM.evmData} width={1200} height={600} />
            </div>
          </div>
          
          {/* 予測指標セクション */}
          <div className="evm-section">
            <h3>完成時予測</h3>
            <div className="evm-forecast-grid">
              <div className="forecast-item">
                <label>BAC (完成時予算):</label>
                <span>{formatCurrency(projectEVM.metrics.bac)}</span>
              </div>
              <div className="forecast-item">
                <label>EAC (完成時コスト予測):</label>
                <span>{formatCurrency(projectEVM.metrics.eac)}</span>
              </div>
              <div className="forecast-item">
                <label>ETC (完成までの予測コスト):</label>
                <span>{formatCurrency(projectEVM.metrics.etc)}</span>
              </div>
              <div className="forecast-item">
                <label>TCPI (完成までに必要な効率指数):</label>
                <span>{projectEVM.metrics.tcpi.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          {/* プロジェクト健全性セクション */}
          <div className={`evm-section evm-health ${interpretation?.overallHealth.toLowerCase()}`}>
            <h3>プロジェクト健全性</h3>
            <div className="health-status">{interpretation?.overallHealth}</div>
            <div className="health-details">
              <p><strong>コスト:</strong> {interpretation?.costStatus}</p>
              <p><strong>スケジュール:</strong> {interpretation?.scheduleStatus}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="evm-empty">
          <p>プロジェクトを選択してEVM分析を開始してください。</p>
          <p>※ タスクが登録されているプロジェクトのみ分析可能です。</p>
        </div>
      )}
    </div>
  );
};
